/**
 * Service Layer Helpers
 *
 * Common utility functions for service layer
 */

import { getClinicId, getOrganisationId } from "@/hooks/useClinicId";

/**
 * Get clinicId with validation (internal use only)
 * @throws Error if clinicId is not available
 * @returns Non-null clinicId
 *
 * 🛡️ SAFETY: This function ensures clinicId is NEVER null
 * If clinicId is missing, it will:
 * 1. Log a detailed error
 * 2. Redirect user to additional-info page
 * 3. Throw an error to prevent further execution
 */
export const requireClinicId = (): string => {
  const clinicId = getClinicId();
  if (!clinicId) {
    const errorMsg = "CRITICAL: Clinic ID is missing.";
    console.error(errorMsg, {
      timestamp: new Date().toISOString(),
      location: window.location.href,
      stack: new Error().stack,
    });

    // Only redirect if not already on safe routes to prevent infinite loops
    if (typeof window !== "undefined") {
      const currentPath = window.location.pathname;
      const safeRoutes = ["/login", "/additional-info", "/404"];

      if (!safeRoutes.some((route) => currentPath.startsWith(route))) {
        console.warn("[requireClinicId] Redirecting to additional-info page");
        window.location.href = "/additional-info";
      }
    }

    throw new Error("Clinic not selected. Please select a clinic in AdditionalInfo page.");
  }
  return clinicId;
};

/**
 * Get organizationId with validation (internal use only)
 * @throws Error if organizationId is not available
 * @returns Non-null organizationId
 *
 * 🛡️ SAFETY: This function ensures organizationId is NEVER null
 */
export const requireOrganizationId = (): string => {
  const organizationId = getOrganisationId();
  if (!organizationId) {
    const errorMsg = "CRITICAL: Organization ID is missing.";
    console.error(errorMsg, {
      timestamp: new Date().toISOString(),
      location: window.location.href,
      stack: new Error().stack,
    });

    // Only redirect if not already on safe routes to prevent infinite loops
    if (typeof window !== "undefined") {
      const currentPath = window.location.pathname;
      const safeRoutes = ["/login", "/additional-info", "/404"];

      if (!safeRoutes.some((route) => currentPath.startsWith(route))) {
        console.warn("[requireOrganizationId] Redirecting to additional-info page");
        window.location.href = "/additional-info";
      }
    }

    throw new Error(
      "Organization not selected. Please select an organization in AdditionalInfo page.",
    );
  }
  return organizationId;
};

/**
 * Get both clinicId and organizationId with validation
 * @throws Error if either is not available
 * @returns Object with both IDs
 */
export const requireClinicContext = (): { clinicId: string; organizationId: string } => {
  const clinicId = requireClinicId();
  const organizationId = requireOrganizationId();
  return { clinicId, organizationId };
};

// ==================== URL Builders ====================

/**
 * Build API URL with automatic clinicId injection
 * @param path - API path (e.g., '/delivery-orders' or '/delivery-orders/123')
 * @param baseApi - Base API path (default: '/api/procedure')
 * @returns Full URL with clinicId
 * @example
 * buildClinicUrl('/delivery-orders')
 * // => '/api/procedure/clinics/123/delivery-orders'
 */
export const buildClinicUrl = (path: string, baseApi = "/api/procedure"): string => {
  const clinicId = requireClinicId();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseApi}/clinics/${clinicId}${cleanPath}`;
};

/**
 * Build API URL with automatic organizationId injection
 * @param path - API path
 * @param baseApi - Base API path (default: '/api/procedure')
 * @returns Full URL with organizationId
 */
export const buildOrgUrl = (path: string, baseApi = "/api/procedure"): string => {
  const organizationId = requireOrganizationId();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseApi}/organisations/${organizationId}${cleanPath}`;
};

/**
 * Build API URL with both organizationId and clinicId
 * @param path - API path
 * @param baseApi - Base API path (default: '/api/procedure')
 * @returns Full URL with both IDs
 * @example
 * buildOrgClinicUrl('/delivery-orders')
 * // => '/api/procedure/organisations/456/clinics/123/delivery-orders'
 */
export const buildOrgClinicUrl = (path: string, baseApi = "/api/procedure"): string => {
  const { organizationId, clinicId } = requireClinicContext();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseApi}/organisations/${organizationId}/clinics/${clinicId}${cleanPath}`;
};

/**
 * Build billing API URL with clinicId (format: /api/billing/clinics/{clinicId}/...)
 * @param path - API path
 * @returns Full billing URL
 */
export const buildBillingUrl = (path: string): string => {
  return buildClinicUrl(path, "/api/billing");
};

/**
 * Build simple billing API URL with clinicId (format: /api/billing/{clinicId}/...)
 * Used for legacy billing APIs that don't have "/clinics/" in the path
 * @param path - API path
 * @returns Simple billing URL
 */
export const buildBillingUrlSimple = (path: string): string => {
  const clinicId = requireClinicId();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/api/billing/${clinicId}${cleanPath}`;
};

/**
 * Build simple procedure API URL with clinicId (format: /api/procedure/{clinicId}/...)
 * Used for procedure APIs that don't have "/clinics/" in the path
 * @param path - API path
 * @returns Simple procedure URL
 */
export const buildProcedureUrl = (path: string): string => {
  const clinicId = requireClinicId();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/api/procedure/${clinicId}${cleanPath}`;
};
/**
 * Build simple procedure API URL with clinicId (format: /api/procedure/clinics/{clinicId}/...)
 * Used for procedure APIs that don't have "/clinics/" in the path
 * @param path - API path
 * @returns Simple procedure URL
 */
export const buildProcedureUrlWithClinics = (path: string): string => {
  const clinicId = requireClinicId();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/api/procedure/clinics/${clinicId}${cleanPath}`;
};

/**
 * Build patient API URL with organizationId
 * @param path - API path
 * @returns Full patient URL
 */
export const buildPatientUrl = (path: string): string => {
  return buildOrgUrl(path, "/api/patient");
};

/**
 * Build patient API URL with organizationId
 * @param path - API path
 * @returns Full patient URL
 */
export const buildPatientUrlwithClinic = (path: string): string => {
  const clinicId = requireClinicId();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/api/patient/${clinicId}${cleanPath}`;
};

/**
 * Build staff management API URL with organizationId and clinicId
 * @param path - API path
 * @returns Full staff management URL (format: /api/staff-management/organisations/{orgId}/hospital-clinics/{clinicId}/...)
 */
export const buildStaffManagementUrl = (path: string): string => {
  const { organizationId, clinicId } = requireClinicContext();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/api/staff-management/organisations/${organizationId}/hospital-clinics/${clinicId}${cleanPath}`;
};

/**
 * Build queue API URL with clinicId only
 * @param path - API path
 * @returns Full queue URL (format: /api/queue/clinics/{clinicId}/...)
 */
export const buildQueueClinicUrl = (path: string): string => {
  return buildClinicUrl(path, "/api/queue");
};

/**
 * Build queue API URL with organizationId and clinicId
 * @param path - API path
 * @returns Full queue URL (format: /api/queue/organisations/{orgId}/clinics/{clinicId}/...)
 */
export const buildQueueOrgClinicUrl = (path: string): string => {
  return buildOrgClinicUrl(path, "/api/queue");
};

/**
 * Build aggregator API URL with clinicId
 * @param path - API path
 * @returns Full aggregator URL (format: /api/aggregator/clinics/{clinicId}/...)
 */

export const buildAggregatorUrl = (path: string): string => {
  return buildClinicUrl(path, "/api/aggregator");
};

/**
 * Build simple aggregator API URL with clinicId
 * @param path - API path
 * @returns Simple aggregator URL (format: /api/aggregator/{clinicId}/...)
 */
export const buildAggregatorUrlSimple = (path: string): string => {
  const clinicId = requireClinicId();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/api/aggregator/${clinicId}${cleanPath}`;
};

/**
 * Build aggregator API URL with organizationId and clinicId
 * @param path - API path
 * @returns Full aggregator URL (format: /api/aggregator/organisations/{orgId}/hospital-clinics/{clinicId}/...)
 */
export const buildAggregatorOrgClinicUrl = (path: string): string => {
  const { organizationId, clinicId } = requireClinicContext();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/api/aggregator/organisations/${organizationId}/hospital-clinics/${clinicId}${cleanPath}`;
};

/**
 * Build appointment API URL
 * @param path - Optional API path segment
 * @returns Full appointment URL (format: /api/appointments/...)
 */
export const buildAppointmentUrl = (path = ""): string => {
  if (!path) {
    return "/api/appointments";
  }
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/api/appointments${cleanPath}`;
};

/**
 * Build block time slot API URL
 * @param path - Optional API path segment
 * @returns Full block time slot URL (format: /api/block-time-slots/...)
 */
export const buildBlockTimeSlotsUrl = (path = ""): string => {
  if (!path) {
    return "/api/block-time-slots";
  }
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/api/block-time-slots${cleanPath}`;
};

/**
 * Build time slots API URL (public holidays etc., routes to VITE_API_URL_APPOINTMENTS)
 * @param path - Optional API path segment
 * @returns Full time slots URL (format: /api/time-slots/...)
 */
export const buildTimeSlotsUrl = (path = ""): string => {
  if (!path) {
    return "/api/time-slots";
  }
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/api/time-slots${cleanPath}`;
};

/**
 * Build appt sms reminder API URL
 * @param path - Optional API path segment
 * @returns Full appt sms reminder URL (format: /api/reminder-template/...)
 */
export const buildApptSmsReminderUrl = (path = ""): string => {
  if (!path) {
    return "/api/reminder-template";
  }
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/api/reminder-template${cleanPath}`;
};
/**
 * Build reports API URL with clinicId
 * @param path - API path
 * @returns Full reports URL (format: /api/reports/clinics/{clinicId}/...)
 */
export const buildReportsUrl = (path: string): string => {
  return buildClinicUrl(path, "/api/reports");
};

/**
 * Build reports API URL with organizationId and clinicId
 * @param path - API path
 * @returns Full reports URL (format: /api/reports/organisations/{orgId}/hospital-clinics/{clinicId}/...)
 */
export const buildReportsOrgClinicUrl = (path: string): string => {
  const { organizationId, clinicId } = requireClinicContext();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/api/reports/organisations/${organizationId}/clinics/${clinicId}${cleanPath}`;
};

/**
 * Build PACS / investigations API URL
 * Gateway path: /pacs-service/api/v1/...
 * @param path - API path (e.g. /investigations)
 */
export const buildPacsUrl = (path = ""): string => {
  const cleanPath = path ? (path.startsWith("/") ? path : `/${path}`) : "";
  return `/pacs-service/api/v1${cleanPath}`;
};
